'use client'

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTheme } from 'styled-components'
import { Swiper as SwiperType } from 'swiper'

import {
  Carat,
  CrossFadeImage,
  RotateClockwise,
  TypeStyle,
  useIsBrowser,
  useSupportsHover,
} from '@syconium/little-miss-figgy'

import {
  SwiperSlideWithAnalytics,
  SwiperWithAnalytics,
} from '../../../../app/_components/swiper/SwiperWithAnalytics'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { useEmbroidery } from '../../../../containers/EmbroideryContainer/EmbroideryProvider'
import { getEmbroideryPlacementKey } from '../../../../containers/EmbroideryContainer/utils/getEmbroideryPlacementKey'
import { EmbroiderableProductFlatlayImage } from '../../../../types/graphql/product'
import { EmbroideryThreadFilter } from '../../Modal/primitives/EmbroideryThreadFilter'
import { LogoPreviewItem } from '../forms/PreviewItems/LogoPreviewItem'

import * as Styles from './EmbroideryModalPreviewCarousel.styles'
import { PreviewFlatlayImageProvider } from './PreviewFlatlayImageContext'

import type { EmbroideryFormKind } from '../../../../containers/EmbroideryContainer/types'

type PreviewMode = 'full' | 'zoom' | 'sleeve'

type PreviewSlide = {
  id: PreviewMode
  flatlayImage: EmbroiderableProductFlatlayImage | undefined
  label: string
  imageSource: string
}

type PreviewSlideContentProps = {
  previewItems: React.ReactNode[]
  slide: PreviewSlide
}

const PREVIEW_IMAGE_ASPECT_RATIOS = {
  sm: 1,
  md: 1,
} as const

const PREVIEW_IMAGE_WIDTHS = {
  unit: 'px',
  xs: 320,
  sm: 360,
  md: 480,
  lg: 550,
  xl: 640,
} as const

function getPreviewModeForFormKind(formKind: EmbroideryFormKind | null): PreviewMode {
  if (formKind === 'ICON') return 'sleeve'
  if (formKind === 'TEXT' || formKind?.includes('LOGO')) return 'zoom'

  return 'full'
}

function buildPreviewSlides({
  fullFlatlayImage,
  fullImageSource,
  zoomFlatlayImage,
  zoomImageSource,
  sleeveFlatlayImage,
  sleeveImageSource,
  labels,
}: {
  fullFlatlayImage: EmbroiderableProductFlatlayImage | undefined
  fullImageSource: string | undefined
  zoomFlatlayImage: EmbroiderableProductFlatlayImage | undefined
  zoomImageSource: string | undefined
  sleeveFlatlayImage: EmbroiderableProductFlatlayImage | undefined
  sleeveImageSource: string | undefined
  labels: Record<PreviewMode, string>
}): PreviewSlide[] {
  if (!fullImageSource) return []

  const slides: PreviewSlide[] = [
    {
      id: 'full',
      flatlayImage: fullFlatlayImage,
      label: labels.full,
      imageSource: fullImageSource,
    },
  ]

  if (zoomImageSource && zoomFlatlayImage) {
    slides.push({
      id: 'zoom',
      flatlayImage: zoomFlatlayImage,
      label: labels.zoom,
      imageSource: zoomImageSource,
    })
  }

  if (sleeveImageSource && sleeveFlatlayImage) {
    slides.push({
      id: 'sleeve',
      flatlayImage: sleeveFlatlayImage,
      label: labels.sleeve,
      imageSource: sleeveImageSource,
    })
  }

  return slides
}

const PreviewSlideContent = ({ previewItems, slide }: PreviewSlideContentProps) => {
  const {
    embroidery: {
      modal: {
        carousel: {
          accessibility: { previewImage: previewImageAlt },
          logoPreviewUnsupported,
        },
      },
    },
  } = useTranslation()
  const {
    embroiderySelections,
    previewItemsByKind,
    selectedFormKind,
    currentPlacement,
    baseCatalogLogoPlacements,
    isB2bEmbroidery,
    isTeamStoreEmbroidery,
  } = useEmbroidery()

  const hasLiveLogoPreview = Boolean(
    (selectedFormKind === 'LOGO_B2B' && previewItemsByKind.LOGO_B2B?.length) ||
      (selectedFormKind === 'LOGO_DTC' && previewItemsByKind.LOGO_DTC?.length)
  )
  const hasSubmittedLogo = Boolean(
    embroiderySelections.LOGO_B2B?.logoImageSource && embroiderySelections.LOGO_B2B.logoName
  )
  const storedLogoPlacement =
    embroiderySelections.LOGO_B2B?.logoPlacement ?? embroiderySelections.LOGO_DTC?.logoPlacement
  const activeLogoPlacement = hasLiveLogoPreview
    ? currentPlacement ?? storedLogoPlacement
    : storedLogoPlacement
  const hasActiveLogo = hasLiveLogoPreview || hasSubmittedLogo

  // Keep preview notice logic aligned with LogoPreviewItem gating.
  const baseCatalogLogoPlacementKeys = baseCatalogLogoPlacements
    ? new Set(baseCatalogLogoPlacements.map(getEmbroideryPlacementKey))
    : null
  // Legacy portals (B2B but not team store) do not define a logo placement because Ops picks it
  // internally, so any preview would just guess the left chest. We surface the notice instead.
  const isPortalEmbroidery = isB2bEmbroidery && !isTeamStoreEmbroidery
  const showLogoPreviewUnsupportedNotice = Boolean(
    hasActiveLogo &&
      (isPortalEmbroidery ||
        (activeLogoPlacement &&
          baseCatalogLogoPlacementKeys &&
          !baseCatalogLogoPlacementKeys.has(getEmbroideryPlacementKey(activeLogoPlacement))))
  )

  return (
    <Styles.PreviewCanvas>
      <PreviewFlatlayImageProvider flatlayImage={slide.flatlayImage}>
        <Styles.PreviewImageFrame>
          <CrossFadeImage
            alt={previewImageAlt}
            aspectRatios={PREVIEW_IMAGE_ASPECT_RATIOS}
            src={slide.imageSource}
            widths={PREVIEW_IMAGE_WIDTHS}
            transparentBackground
          />
          {previewItems.map((previewItem, previewItemIndex) => {
            return <Fragment key={previewItemIndex}>{previewItem}</Fragment>
          })}
          {showLogoPreviewUnsupportedNotice ? (
            <Styles.LogoPreviewUnsupportedNotice>
              <TypeStyle.BodyNano>{logoPreviewUnsupported}</TypeStyle.BodyNano>
            </Styles.LogoPreviewUnsupportedNotice>
          ) : null}
        </Styles.PreviewImageFrame>
      </PreviewFlatlayImageProvider>
    </Styles.PreviewCanvas>
  )
}

export const EmbroideryModalPreviewCarousel = () => {
  const {
    embroidery: {
      modal: {
        carousel: { accessibility: embroideryCarouselAccessibility, previewModes },
      },
    },
  } = useTranslation()
  const {
    embroiderySelections,
    productEmbroideryConfig,
    productFlatlayImage,
    productEmbroideryOptionKeys,
    previewItemsByKind,
    selectedFormKind,
  } = useEmbroidery()
  const isBrowser = useIsBrowser()
  const hoverSupported = useSupportsHover()
  const theme = useTheme()

  const [selectedSlideId, setSelectedSlideId] = useState<PreviewMode>('full')
  const swiperRef = useRef<SwiperType>(null)

  const previewItems = [
    ...Object.values(previewItemsByKind).flat(),
    ...(embroiderySelections.LOGO_B2B && !previewItemsByKind.LOGO_B2B
      ? [<LogoPreviewItem key='logo-b2b-preview' values={embroiderySelections.LOGO_B2B} />]
      : []),
  ]

  const fullFlatlayImage = productEmbroideryConfig?.flatlay ?? productFlatlayImage
  const fullImageSource = fullFlatlayImage?.image.url

  const sleeveFlatlayImage = productEmbroideryOptionKeys.includes('ICON')
    ? productEmbroideryConfig?.locationFlatlays.find(flatlay => flatlay.location === 'SLEEVE')
    : undefined

  const zoomFlatlayImage = productEmbroideryConfig?.locationFlatlays.find(
    flatlay => flatlay.location === 'CHEST'
  )

  const zoomImageSource = zoomFlatlayImage?.image.url
  const sleeveImageSource = sleeveFlatlayImage?.image.url

  const slides = useMemo(
    () =>
      buildPreviewSlides({
        fullFlatlayImage,
        fullImageSource,
        zoomFlatlayImage,
        zoomImageSource,
        sleeveFlatlayImage,
        sleeveImageSource,
        labels: previewModes,
      }),
    [
      fullFlatlayImage,
      fullImageSource,
      previewModes,
      sleeveFlatlayImage,
      sleeveImageSource,
      zoomFlatlayImage,
      zoomImageSource,
    ]
  )

  const slidesRef = useRef(slides)

  const handleBeforeSwiperInit = useCallback((swiper: SwiperType) => {
    swiperRef.current = swiper
  }, [])

  const handleSwiperSlideChange = useCallback(
    (swiper: SwiperType) => {
      // In loop mode, Swiper duplicates edge slides internally, and `realIndex` maps the active Swiper
      // slide back to the matching item in our original `slides` array.
      const activeSlide = slides[swiper.realIndex]
      if (!activeSlide) return

      setSelectedSlideId(currentSelectedSlideId =>
        currentSelectedSlideId === activeSlide.id ? currentSelectedSlideId : activeSlide.id
      )
    },
    [slides]
  )

  slidesRef.current = slides

  // Synchronize the selected form kind with the corresponding slide.
  // Text|Logo -> Zoom; Icon -> Sleeve; None -> Full
  // Uses slidesRef so the effect only re-runs when the form kind changes,
  // not when slide image URLs change (e.g. color selection), keeping the
  // user on whichever slide they're currently viewing.
  useEffect(() => {
    const targetSlideId = getPreviewModeForFormKind(selectedFormKind)
    const targetSlideIndex = Math.max(
      slidesRef.current.findIndex(slide => slide.id === targetSlideId),
      0
    )

    const nextSlide = slidesRef.current[targetSlideIndex]
    if (!nextSlide) return

    setSelectedSlideId(nextSlide.id)
    swiperRef.current?.slideToLoop(targetSlideIndex, undefined, false)
  }, [selectedFormKind])

  if (slides.length === 0) {
    return (
      <Styles.PreviewRoot>
        <Styles.PreviewShell>
          <Styles.PreviewCanvas>
            <Styles.PreviewImageFrame />
          </Styles.PreviewCanvas>
        </Styles.PreviewShell>
      </Styles.PreviewRoot>
    )
  }

  const currentSlide = slides.find(slide => slide.id === selectedSlideId) ?? slides[0]!
  const currentSlideIndex = slides.findIndex(slide => slide.id === currentSlide.id)
  const hasMultipleSlides = slides.length > 1

  if (!hasMultipleSlides) {
    return (
      <Styles.PreviewRoot>
        <Styles.PreviewShell>
          <PreviewSlideContent previewItems={previewItems} slide={currentSlide} />
        </Styles.PreviewShell>
      </Styles.PreviewRoot>
    )
  }

  const goToAdjacentSlide = (direction: -1 | 1) => {
    if (direction === 1) {
      swiperRef.current?.slideNext(undefined, false)
    } else {
      swiperRef.current?.slidePrev(undefined, false)
    }
  }

  const slideDots = slides.map(slide => {
    const isSelected = slide.id === currentSlide.id
    return <Styles.Dot $isSelected={isSelected} aria-hidden='true' key={slide.id} />
  })

  return (
    <Styles.PreviewRoot>
      <EmbroideryThreadFilter />
      <Styles.DesktopDots aria-hidden='true'>{slideDots}</Styles.DesktopDots>

      <Styles.PreviewShell>
        {isBrowser ? (
          <SwiperWithAnalytics
            initialSlide={currentSlideIndex}
            loop={hasMultipleSlides}
            simulateTouch={!hoverSupported}
            slidesPerView={1}
            spaceBetween={0}
            speed={400}
            onBeforeInit={handleBeforeSwiperInit}
            onSlideChange={handleSwiperSlideChange}
          >
            {slides.map(slide => {
              return (
                <SwiperSlideWithAnalytics key={slide.id}>
                  <PreviewSlideContent previewItems={previewItems} slide={slide} />
                </SwiperSlideWithAnalytics>
              )
            })}
          </SwiperWithAnalytics>
        ) : (
          // Keep a static preview for non-browser renders
          <PreviewSlideContent previewItems={previewItems} slide={currentSlide} />
        )}
        <Styles.MobileDots aria-hidden='true'>{slideDots}</Styles.MobileDots>

        <Styles.DesktopArrowControls>
          <Styles.DesktopArrowButton
            aria-label={embroideryCarouselAccessibility.previousPreviewImage}
            data-direction='left'
            onClick={() => goToAdjacentSlide(-1)}
            type='button'
          >
            <Carat rotateClockwise={RotateClockwise.Half} />
          </Styles.DesktopArrowButton>

          <Styles.DesktopArrowButton
            aria-label={embroideryCarouselAccessibility.nextPreviewImage}
            data-direction='right'
            onClick={() => goToAdjacentSlide(1)}
            type='button'
          >
            <Carat stroke={theme.color.text.primary.on.background} />
          </Styles.DesktopArrowButton>
        </Styles.DesktopArrowControls>
      </Styles.PreviewShell>

      <Styles.DesktopDots $withTopMargin aria-hidden='true'>
        {slideDots}
      </Styles.DesktopDots>
    </Styles.PreviewRoot>
  )
}
